import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import EditConsumerForm from "./EditConsumerForm";
import ConsumerNotes from "../MyConsumers/ConsumerNotes";
import { AuthContext } from "../../contexts/AuthContext";

const ConsumerEditor = ({ consumerId, onCancel, onUpdated }) => {
  const [consumer, setConsumer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const fetchConsumer = async () => {
    if (!user?.tenant_id) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await secureAxios.get(
        `${apiBase}/api/consumers/${consumerId}`,
        {
          params: { tenant_id: user.tenant_id },
        }
      );

      console.log("âœ… Consumer data fetched");
      setConsumer(res.data);
    } catch (err) {
      console.error("âŒ Error fetching consumer:", err);
      if (err.response?.status === 404) {
        setError("Consumer not found or access denied.");
      } else if (err.response?.status === 403) {
        setError("You do not have permission to access this consumer record.");
      } else {
        setError("Failed to load consumer details. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!consumerId) {
      setError("No consumer ID provided");
      setLoading(false);
      return;
    }

    // Fetch consumer data immediately
    fetchConsumer();
  }, [consumerId]);

  if (loading) {
    return (
      <div className="card card-body shadow-sm">
        <div className="text-center p-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">
              Loading consumer information...
            </span>
          </div>
          <p className="mt-2">Loading consumer information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card card-body shadow-sm">
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle"></i> {error}
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-secondary" onClick={onCancel}>
            Go Back
          </button>
          <button className="btn btn-primary" onClick={fetchConsumer}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-body shadow-sm">
      {/* PHI Access Notice - Simplified informational notice */}
      <div className="alert alert-info mb-3" role="alert">
        <i className="bi bi-shield-check"></i>{" "}
        <strong>Protected Health Information</strong>
        <br />
        <small>
          All access and modifications to this consumer record are logged for
          HIPAA compliance. Access time: {new Date().toLocaleString()}
        </small>
      </div>

      {consumer ? (
        <>
          <EditConsumerForm
            consumer={consumer}
            onCancel={onCancel}
            onUpdated={() => {
              console.log("✅ Consumer updated successfully");
              onUpdated();
            }}
          />
          {/* Consumer Notes - Outside the form to prevent nested form issues */}
          <div className="mt-4">
            <ConsumerNotes
              consumerId={consumerId}
              consumerName={consumer?.name}
            />
          </div>
        </>
      ) : (
        <div className="alert alert-info">
          Consumer record not found or you do not have access to this record.
        </div>
      )}
    </div>
  );
};

export default ConsumerEditor;
